/**
 * HLOS-222 — Feedback Pinia store (zero PII por default).
 *
 * Funcionalidades:
 * - `draft`: rascunho de mensagem (não autoenvia; usuário revisa).
 * - `submit(category, message)`: enfileira para envio assíncrono.
 * - `pending`: lista de feedbacks não enviados (auditoria).
 *
 * Constitucional:
 * - CL-08 (Ética fechada): NUNCA armazena user_id, IP, device_id.
 * - Apenas `category` (enum) + `message` (texto livre) + `timestamp` (epoch).
 *
 * Skills HLOS governando:
 * - vue-shell: Composition API + Pinia
 * - constitutional-lock: gate CL-08 (zero PII)
 * - companion-product: feedback respeitoso (zero coercive)
 */

import { defineStore } from "pinia";
import { computed, ref } from "vue";

/**
 * Categorias permitidas (enum fechado — LGPD minimiza dados).
 * "other" é fallback genérico; nenhuma categoria identifica usuário.
 */
const ALLOWED_CATEGORIES = [
  "general",
  "ui",
  "performance",
  "feature-request",
  "other",
] as const;

export type FeedbackCategory = (typeof ALLOWED_CATEGORIES)[number];

interface PendingFeedback {
  readonly category: FeedbackCategory;
  readonly message: string;
  readonly ts: number; // epoch ms local
}

export const useFeedbackStore = defineStore("feedback", () => {
  const draft = ref<string>("");
  const pending = ref<PendingFeedback[]>([]);

  function setDraft(text: string): void {
    draft.value = text;
  }

  /**
   * Submete feedback. NÃO envia automaticamente — armazena localmente
   * para envio assíncrono (v0.2 com endpoint configurável).
   *
   * LGPD: zero PII armazenado (sem user_id, IP, device_id).
   */
  function submit(category: FeedbackCategory, message: string): void {
    pending.value.push({
      category,
      message,
      ts: Date.now(),
    });
    draft.value = "";
    // Em v0.2: enviar via POST endpoint configurável.
  }

  /** Valida se categoria é permitida (defesa em profundidade). */
  function isAllowedCategory(category: string): boolean {
    return (ALLOWED_CATEGORIES as readonly string[]).includes(category);
  }

  const allowedCategories = computed(() => [...ALLOWED_CATEGORIES]);

  /** Limpa fila após envio (chamado após POST bem-sucedido). */
  function clearPending(): void {
    pending.value = [];
  }

  return {
    draft,
    pending,
    allowedCategories,
    setDraft,
    submit,
    isAllowedCategory,
    clearPending,
  };
});
